// Right rail "Customer" tab body (docs/06-ux-rules.md "Right rail" /
// "Look-back rail (FR-63)"; veda:projects/unified-feedback-inbox/parts/
// p08-right-rail.md D2; VISUAL-SPEC.md §4 "Customer tab"). Renders its own
// heading + "View profile" link row, with a real `ExternalLink` icon (not a
// raw arrow glyph) after the text — design puts the link inline with the
// heading, not below the field list, unlike `components/CustomerInfoCard`'s
// own chrome — followed by a single-column `KeyValueList`: `FieldGrid`'s
// hard-wired 2-column grid squishes into the rail's 340px width. On a
// RecoveryAI case the link folds into a "..." menu with Restrict (FR-67).
import { FeedbackInboxDetailCustomer } from 'api/feedbackInbox.types'
import ExternalLink from 'components/Icons/ExternalLink'
import KeyValueList, { KeyValueListItem } from 'components/KeyValueList'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import { currencyFormatter } from 'utilities/formatters'
import { customerProfileUrl } from 'utilities/urlUtils'
import CustomerActionsMenu from './CustomerActionsMenu'
import RailSectionHeading from './RailSectionHeading'

const t = buildTranslate('feedback_inbox.rail.customer')

export interface CustomerPanelProps {
  customer: FeedbackInboxDetailCustomer
  // `lookback` swaps Email/Phone/Prior-resolution-history for the FR-63
  // 90-day aggregates (docs/06-ux-rules.md "Look-back rail (FR-63)").
  mode: 'live' | 'lookback'
  // Set on a restrict-eligible (RecoveryAI) case — swaps the inline profile
  // link for the "..." menu. Live mode only.
  onRestrict?: () => void
  restrictDisabledReason?: string
}

const CustomerPanel = ({
  customer,
  mode,
  onRestrict,
  restrictDisabledReason,
}: CustomerPanelProps) => {
  const [css] = useStyletron()
  const ability = useAbility()
  const canViewProfile = ability.can('access', 'UsersByIdentifier')
  const profileTo =
    customer.uid && canViewProfile ? customerProfileUrl(customer.uid) : null
  const isLive = mode === 'live'
  const showActionsMenu = isLive && !!onRestrict
  const showProfileLink = isLive && !showActionsMenu && !!profileTo

  const items: KeyValueListItem[] = useMemo(() => {
    // Non-identifying aggregates — always present regardless of PII consent,
    // and shared by both the live and look-back field sets.
    const base: KeyValueListItem[] = [
      // Split to field-order parity with `components/CustomerInfoCard`
      // (AI-1332) — First name/Last name as two independent rows, each
      // falling back to the panel's own em-dash convention, rather than a
      // single combined `name` row.
      { label: t('first_name'), value: customer.firstName ?? t('pii_hidden') },
      { label: t('last_name'), value: customer.lastName ?? t('pii_hidden') },
      {
        label: t('loyalty_tier'),
        value: customer.loyaltyTier?.name ?? t('pii_hidden'),
      },
      { label: t('total_visits'), value: String(customer.totalVisits) },
      {
        label: t('lifetime_spend'),
        // No cents on a lifetime aggregate (matches CustomerInfoCard's own
        // `currencyFormatter(customer.lifetimeSpend, false)` precedent).
        value: currencyFormatter(customer.lifetimeSpendCents / 100, false),
      },
    ]

    if (mode === 'lookback') {
      return [
        ...base,
        {
          label: t('thumbs_down_90d'),
          value: customer.thumbsDown90d
            ? t('thumbs_down_90d_value', {
                count: customer.thumbsDown90d.count,
                amount: currencyFormatter(
                  customer.thumbsDown90d.rewardedCents / 100,
                  false
                ),
              })
            : t('value_unavailable'),
        },
        {
          label: t('returned_after'),
          value: customer.returnedAfter
            ? t('returned_after_value', {
                returned: customer.returnedAfter.returned,
                total: customer.returnedAfter.total,
              })
            : t('value_unavailable'),
        },
      ]
    }

    return [
      ...base,
      { label: t('email'), value: customer.email ?? t('pii_hidden') },
      { label: t('phone'), value: customer.phone ?? t('pii_hidden') },
      {
        label: t('prior_resolution_history'),
        value: t('prior_resolution_history_value', {
          count: customer.priorResolutionCount,
        }),
      },
    ]
  }, [customer, mode])

  return (
    <div>
      {/* In-body heading is intentional here — VISUAL-SPEC.md §4. The rows
          draw their own rules, so the panel has no bottom border. */}
      <RailSectionHeading title={t('title')}>
        {customer.restricted && (
          <span
            className={css({
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: 1.4,
              color: palette.cayenne50,
              backgroundColor: palette.cayenne05,
            })}
          >
            {t('restricted_pill')}
          </span>
        )}
        {/* No profile link or menu in look-back mode — the FR-63 field set
            replaces it along with Email/Phone/Prior-resolution-history (P8
            D2 scopes the link to the LIVE customer panel only). */}
        {showActionsMenu && onRestrict && (
          <div className={css({ marginLeft: 'auto' })}>
            <CustomerActionsMenu
              profileTo={profileTo}
              restrictDisabledReason={restrictDisabledReason}
              onRestrictClick={onRestrict}
            />
          </div>
        )}
        {showProfileLink && profileTo && (
          <Link
            to={profileTo}
            className={css({
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              marginLeft: 'auto',
              fontWeight: 700,
              fontSize: '12px',
              color: palette.belizeHole,
            })}
          >
            {t('view_profile')}
            <ExternalLink
              className={css({ width: '12px', height: '12px' })}
              color={palette.belizeHole}
            />
          </Link>
        )}
      </RailSectionHeading>
      <KeyValueList items={items} ariaLabel={t('title')} />
    </div>
  )
}

export default CustomerPanel
